import { ZeitauswahlChange } from './zeitauswahl/zeitauswahl-change';

/** Zeile mit Umsatz des Vorjahreszeitraums und prozentualer Änderung. */
export interface MitUmsatzVorjahr {
    umsatz: number;
    umsatz_vorjahr?: number;
    /** null, wenn im Vorjahr kein Umsatz zum Vergleich da war. */
    umsatz_delta_prozent?: number | null;
    /** Umsatz nur im Vorjahr, im aktuellen Zeitraum keiner. */
    nur_vorjahr?: boolean;
}

export type UmsatzVorjahrFilter = 'alle' | 'weniger' | 'stark' | 'weg';

export class ReportUtils {
    public static getVorjahreszeitraum(input: ZeitauswahlChange): ZeitauswahlChange {
        const von = new Date(input.von);
        const bis = new Date(input.bis);
        let vonVorjahr: Date;
        let bisVorjahr: Date;
        if (von.getDate() === 29 && von.getMonth() === 1) {
            vonVorjahr = new Date(von.getFullYear() - 1, 1, 28);
        } else {
            vonVorjahr = new Date(von.getFullYear() - 1, von.getMonth(), von.getDate());
        }
        if (bis.getDate() === 29 && bis.getMonth() === 1) {
            bisVorjahr = new Date(bis.getFullYear() - 1, 1, 28);
        } else {
            bisVorjahr = new Date(bis.getFullYear() - 1, bis.getMonth(), bis.getDate());
        }
        return {
            von: vonVorjahr,
            bis: bisVorjahr,
            aggregieren: input.aggregieren,
            aktuellerZeitinterval: input.aktuellerZeitinterval,
        };
    }

    /**
     * Prozentänderung zum Vorjahresumsatz. −100 = im aktuellen Zeitraum kein Umsatz mehr.
     * null, wenn der Vorjahresumsatz 0 ist (kein Vergleich, z. B. neuer Kunde).
     */
    public static umsatzAenderungProzent(umsatz: number, umsatzVorjahr: number): number | null {
        const aktuell = Number(umsatz) || 0;
        const vorjahr = Number(umsatzVorjahr) || 0;
        if (vorjahr <= 0) {
            return null;
        }
        return Math.round(((aktuell - vorjahr) / vorjahr) * 10000) / 100;
    }

    public static passtUmsatzVorjahrFilter(row: MitUmsatzVorjahr, filter: string): boolean {
        if (!filter || filter === 'alle') {
            return !row.nur_vorjahr;
        }
        const umsatz = Number(row.umsatz) || 0;
        const vorjahr = Number(row.umsatz_vorjahr) || 0;
        const delta = row.umsatz_delta_prozent;
        if (filter === 'weg') {
            return vorjahr > 0 && umsatz <= 0;
        }
        if (filter === 'stark') {
            return delta != null && delta <= -50;
        }
        if (filter === 'weniger') {
            return delta != null && delta < 0;
        }
        return true;
    }

    /** Aktuelle Zeilen plus Vorjahr-Zeilen ohne Umsatz im aktuellen Zeitraum. */
    public static mergeUmsatzVorjahr<T extends { umsatz: number }>(
        aktuell: T[],
        vorjahr: T[],
        idOf: (row: T) => unknown,
        ohneAktuellenUmsatz: (row: T) => T,
    ): T[] {
        const keyOf = (id: unknown): string => {
            const n = Number(id);
            return Number.isFinite(n) ? String(n) : String(id ?? '');
        };
        const vorjahrById = new Map<string, T>();
        for (const row of vorjahr) {
            vorjahrById.set(keyOf(idOf(row)), row);
        }
        const seen = new Set<string>();
        const merged: T[] = [];
        for (const row of aktuell) {
            const key = keyOf(idOf(row));
            seen.add(key);
            const prev = vorjahrById.get(key);
            merged.push(ReportUtils.mitVorjahr(row, prev ? Number(prev.umsatz) || 0 : 0));
        }
        for (const row of vorjahr) {
            const key = keyOf(idOf(row));
            if (seen.has(key) || (Number(row.umsatz) || 0) <= 0) {
                continue;
            }
            const gone = ReportUtils.mitVorjahr(ohneAktuellenUmsatz(row), Number(row.umsatz) || 0);
            gone.nur_vorjahr = true;
            merged.push(gone);
        }
        return merged;
    }

    public static ohneAktuellenUmsatz<T extends { umsatz: number }>(row: T): T {
        return {
            ...row,
            umsatz: 0,
            umsatz_prozent: 0,
            wareneinsatz: 0,
            wareneinsatz_prozent: 0,
            rohertrag: 0,
            rohertrag_prozent: 0,
            abgabebelege: 0,
            details: [],
        } as T;
    }

    private static mitVorjahr<T extends { umsatz: number }>(row: T, umsatzVorjahr: number): T & MitUmsatzVorjahr {
        const umsatz = Number(row.umsatz) || 0;
        return {
            ...row,
            umsatz_vorjahr: umsatzVorjahr,
            umsatz_delta_prozent: ReportUtils.umsatzAenderungProzent(umsatz, umsatzVorjahr),
        } as T & MitUmsatzVorjahr;
    }
}
